import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { User, Users, UserRound } from 'lucide-react';
import Modal from './modal';
import SimpleDropdown from './SimpleDropdown';
import { listStoreAccounts, createStorePreviewSession } from '../utils/queries';

const MODES = [
	{ value: 'me', label: 'Myself (me)', description: 'Log in to the storefront as you.', icon: User },
	{
		value: 'user',
		label: 'Impersonate registered user',
		description: 'Log in as one of this store’s registered customers.',
		icon: Users,
	},
	{ value: 'guest', label: 'Guest user', description: 'Browse with no account, as a guest checkout.', icon: UserRound },
];

// Launches the storefront in a new tab as a chosen user, from StoreSwitcher's
// new launch button. Card layout mirrors pi_ecommerce_store's checkout
// payment-method selector (a label wrapping a visually-hidden radio input).
export default function LaunchStoreModal({ open, setOpen, storeId, storeName, allowGuestCheckout }) {
	const [mode, setMode] = useState('me');
	const [accounts, setAccounts] = useState([]);
	const [accountsLoading, setAccountsLoading] = useState(false);
	const [customerAccountId, setCustomerAccountId] = useState(null);
	const [launching, setLaunching] = useState(false);

	useEffect(() => {
		if (!open) return;
		setMode('me');
		setCustomerAccountId(null);
	}, [open]);

	useEffect(() => {
		if (!open || mode !== 'user') return;
		let cancelled = false;
		setAccountsLoading(true);
		listStoreAccounts({ storeId, limit: 200 }).then((result) => {
			if (cancelled) return;
			setAccountsLoading(false);
			if (!result?.success) {
				toast.error(result?.error?.message || 'Failed to load registered users.');
				return;
			}
			const rows = result.data.store_accounts || [];
			setAccounts(rows);
			setCustomerAccountId((current) => current ?? rows[0]?.customerAccountId ?? null);
		});
		return () => {
			cancelled = true;
		};
	}, [open, mode, storeId]);

	const modes = MODES.filter((m) => m.value !== 'guest' || allowGuestCheckout);

	const handleLaunch = async () => {
		if (mode === 'user' && !customerAccountId) {
			toast.error('Choose a registered user to impersonate.');
			return;
		}
		setLaunching(true);
		const result = await createStorePreviewSession(storeId, {
			mode,
			...(mode === 'user' ? { customerAccountId } : {}),
		});
		setLaunching(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to launch store.');
			return;
		}
		window.open(result.data.launchUrl, '_blank', 'noopener,noreferrer');
		setOpen(false);
	};

	return (
		<Modal open={open} setOpen={setOpen} title={`Launch ${storeName || 'Store'}`} size='md'>
			<p className='text-sm text-gray-500'>Open the storefront in a new tab, logged in as:</p>

			<div className='mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3'>
				{modes.map(({ value, label, description, icon: Icon }) => {
					const isSelected = mode === value;
					return (
						<label
							key={value}
							className={`flex flex-col items-center gap-2 rounded-lg border px-4 py-4 text-sm font-medium text-center cursor-pointer transition-colors ${
								isSelected ? 'border-gray-900 border-2' : 'border-gray-200 hover:bg-gray-50'
							}`}
						>
							<Icon className='size-6 text-gray-700' />
							<span>{label}</span>
							<span className='text-xs font-normal text-gray-500'>{description}</span>
							<input
								type='radio'
								name='previewMode'
								checked={isSelected}
								onChange={() => setMode(value)}
								className='sr-only'
							/>
						</label>
					);
				})}
			</div>

			{mode === 'user' && (
				<div className='mt-4'>
					{accountsLoading ? (
						<p className='text-sm text-gray-400 py-2'>Loading registered users…</p>
					) : accounts.length === 0 ? (
						<p className='text-sm text-gray-400 py-2'>No registered users found on this store.</p>
					) : (
						<SimpleDropdown
							value={customerAccountId}
							onChange={setCustomerAccountId}
							className='w-full'
							options={accounts.map((a) => ({
								value: a.customerAccountId,
								label: [a.firstName, a.lastName].filter(Boolean).join(' ')
									? `${[a.firstName, a.lastName].filter(Boolean).join(' ')} (${a.email})`
									: a.email,
							}))}
						/>
					)}
				</div>
			)}

			<div className='flex justify-start gap-2 pt-6'>
				<button type='button' className='btn-secondary' onClick={() => setOpen(false)} disabled={launching}>
					Cancel
				</button>
				<button
					type='button'
					className='btn-primary'
					onClick={handleLaunch}
					disabled={launching || (mode === 'user' && (accountsLoading || !customerAccountId))}
				>
					{launching ? 'Launching...' : 'Launch Store'}
				</button>
			</div>
		</Modal>
	);
}
